'use client';

import { useCallback, useEffect } from 'react';
import Link from 'next/link';
import { useTranslation } from '@/core/i18n';
import { logger } from '@/core/lib/logger';
import { isStaleChunkError } from '@/core/lib/stale-chunk';

export default function PublicError({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation();
  
  useEffect(() => {
    logger.error('Route error boundary caught error', {
      component: 'PublicError',
      stack: error.stack,
    });
  }, [error]);

  const handleRetry = useCallback(() => { reset(); }, [reset]);

  return (
    <div id="public-error-page" className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="w-14 h-14 rounded-full bg-hms-red/10 border border-hms-red/20 flex items-center justify-center mx-auto mb-5">
          <svg className="w-7 h-7 text-hms-red" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-hms-text mb-2">{t('errorPage.title')}</h2>
        <p className="text-sm text-hms-textDim mb-6">{isStaleChunkError(error)
            ? 'A new version of the app was released while this page was open. '
              + 'Reload the page to continue — your work is not lost.'
            : error.message || t('errorPage.message')}</p>
        <div className="flex items-center justify-center gap-3">
          <button id="public-error-retry" type="button" onClick={handleRetry}
            className="px-5 py-2.5 rounded-lg bg-hms-gold text-black text-sm font-bold hover:bg-hms-goldLt transition-colors">
            {t('errorPage.tryAgain')}
          </button>
          <Link id="public-error-home" href="/"
            className="px-5 py-2.5 rounded-lg border border-hms-border text-sm font-semibold text-hms-textSub hover:text-hms-text transition-colors">
            {t('errorPage.goHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
